import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useTheme, IconButton, Tooltip, Collapse } from '@mui/material';
import { Typography, Box } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

interface DragAndDropMobileProps {
  htmlContent: string;
  onVerify?: (result: boolean) => void;
  onSubmit: () => void;
  /** Fallback, falls im Markup keine data-multiple steht (Default: true) */
  multiple?: boolean;
  onSaveProgress?: (data: { placedElements?: Record<string, string[]>; selectedOptions?: string[]; points: number }) => void;
  data?: { placedElements?: Record<string, string[]>; selectedOptions?: string[]; points: number };
}

interface DropZone {
  id: string;
  label: string;
  multiple: boolean;
}

type ItemState = 'correct' | 'incorrect' | 'partial' | null;

const shuffleArray = (array: { id: string; text: string }[]) =>
  [...array]
    .map((item) => ({ item, sort: Math.random() }))
    .sort((a, b) => a.sort - b.sort)
    .map(({ item }) => item);

const triggerHapticFeedback = (type: 'light' | 'medium' | 'heavy' = 'medium') => {
  if ('vibrate' in navigator) {
    const patterns = { light: [10], medium: [30], heavy: [50] };
    navigator.vibrate(patterns[type]);
  }
};

const DragAndDropMobile: React.FC<DragAndDropMobileProps> = ({
  htmlContent,
  onVerify,
  onSubmit,
  multiple: multipleProp,
  onSaveProgress,
  data,
}) => {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  // --- State ---
  const [showInstructions, setShowInstructions] = useState(false);
  const [selectedElement, setSelectedElement] = useState<string | null>(null);
  const [zones, setZones] = useState<Record<string, string[]>>({});
  const [availableElements, setAvailableElements] = useState<string[]>([]);
  const [feedback, setFeedback] = useState<Record<string, ItemState>>({});
  const [points, setPoints] = useState<number>(0);
  const [hasVerified, setHasVerified] = useState<boolean>(false);

  // --- Parse HTML ---
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, 'text/html');

  const dragElements = useMemo(
    () =>
      shuffleArray(
        Array.from(doc.querySelectorAll('.diadibi-drag-element')).map((el) => ({
          id: el.id,
          text: el.textContent?.trim() || '',
        }))
      ),
    [htmlContent]
  );

  const dropZones: DropZone[] = useMemo(() => {
    return Array.from(doc.querySelectorAll('.diadibi-drop-zone')).map((el) => {
      const id = el.id.trim();
      const label = el.previousElementSibling?.textContent?.trim() || '';
      const attr = el.getAttribute('data-multiple');
      const multiple =
        attr != null ? attr !== 'false' : typeof multipleProp === 'boolean' ? multipleProp : true;
      return { id, label, multiple };
    });
  }, [doc, multipleProp]);

  // --- Init / Restore ---
  useEffect(() => {
    if (data?.placedElements && Object.keys(data.placedElements).length > 0) {
      setZones(data.placedElements);
      setPoints(data.points || 0);
      setHasVerified(true);
      const placedIds = Object.values(data.placedElements).flat();
      setAvailableElements(dragElements.map((el) => el.id).filter((id) => !placedIds.includes(id)));
    } else {
      setZones(Object.fromEntries(dropZones.map((z) => [z.id, []])));
      setAvailableElements(dragElements.map((el) => el.id));
      setHasVerified(false);
      setFeedback({});
      setPoints(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [htmlContent]);

  const hasAnyAssignments = useMemo(() => Object.values(zones).some((a) => a.length > 0), [zones]);
  const totalAssigned = useMemo(() => Object.values(zones).flat().length, [zones]);

  // --- Interaktion ---
  const selectElement = (elementId: string) => {
    if (hasVerified) return;
    setSelectedElement((prev) => (prev === elementId ? null : elementId));
    triggerHapticFeedback('light');
  };

  const placeInZone = (zoneId: string) => {
    if (!selectedElement || hasVerified) return;

    const newZones = { ...zones };
    const zone = dropZones.find((z) => z.id === zoneId);

    if (zone?.multiple) {
      if (!newZones[zoneId].includes(selectedElement)) {
        newZones[zoneId] = [...newZones[zoneId], selectedElement];
      }
    } else {
      if (newZones[zoneId].length > 0) {
        const replaced = newZones[zoneId][0];
        setAvailableElements((prev) => [...prev, replaced]);
      }
      newZones[zoneId] = [selectedElement];
    }

    Object.keys(newZones).forEach((other) => {
      if (other !== zoneId) newZones[other] = newZones[other].filter((id) => id !== selectedElement);
    });

    setZones(newZones);
    setAvailableElements((prev) => prev.filter((id) => id !== selectedElement));
    setSelectedElement(null);
    triggerHapticFeedback('medium');
  };

  const removeFromZone = (elementId: string, zoneId: string) => {
    if (hasVerified) return;
    const newZones = { ...zones };
    newZones[zoneId] = newZones[zoneId].filter((id) => id !== elementId);
    setZones(newZones);
    setAvailableElements((prev) => [...prev, elementId]);
    triggerHapticFeedback('light');
  };

  const resetAll = () => {
    if (hasVerified) return;
    setZones(Object.fromEntries(dropZones.map((z) => [z.id, []])));
    setAvailableElements(dragElements.map((el) => el.id));
    setSelectedElement(null);
    setFeedback({});
    setPoints(0);
    triggerHapticFeedback('medium');
  };

  // --- Theme-basierte Farben ---
  const zoneColors = (state: ItemState, interactive: boolean) => {
    if (state === 'correct') return { 
      bg: theme.palette.success.main + '20', // 20% opacity
      border: theme.palette.success.main 
    };
    if (state === 'partial') return { 
      bg: theme.palette.grey[200] + '30', // F6C973 with opacity
      border: theme.palette.grey[200] 
    };
    if (state === 'incorrect') return { 
      bg: theme.palette.error.main + '20',
      border: theme.palette.error.main 
    };
    if (interactive) return { 
      bg: theme.palette.info.main + '15',
      border: theme.palette.info.main 
    };
    return { 
      bg: theme.palette.grey[400], // F5F5F5
      border: theme.palette.secondary.dark // DDDDDD
    };
  };

  // --- Verify ---
  const handleVerify = () => {
    if (!hasAnyAssignments) return;

    setHasVerified(true);

    let score = 0;
    const newFeedback: Record<string, 'correct' | 'incorrect' | 'partial'> = {};

    dropZones.forEach((zone) => {
      const correctItems = Array.from(
        doc.querySelectorAll(`.diadibi-drag-element[correct-zone="${zone.id}"]`)
      ).map((el) => el.id);

      const itemsInZone = zones[zone.id] || [];

      itemsInZone.forEach((itemId) => {
        const ok = correctItems.includes(itemId);
        newFeedback[itemId] = ok ? 'correct' : 'incorrect';
        if (ok) score += 1;
      });

      if (zone.multiple) {
        const correctCount = itemsInZone.filter((id) => correctItems.includes(id)).length;
        const allCorrectPlaced = correctCount === itemsInZone.length && correctCount > 0;
        const allAndOnlyCorrect = allCorrectPlaced && itemsInZone.length === correctItems.length;
        newFeedback[zone.id] = allAndOnlyCorrect ? 'correct' : correctCount > 0 ? 'partial' : 'incorrect';
      } else {
        const item = itemsInZone[0];
        const isCorrect = !!item && correctItems.includes(item);
        newFeedback[zone.id] = isCorrect ? 'correct' : 'incorrect';
        if (item && newFeedback[item] === undefined) {
          newFeedback[item] = isCorrect ? 'correct' : 'incorrect';
        }
      }
    });

    setFeedback(newFeedback);
    setPoints(Math.max(0, score));

    onSaveProgress?.({
      placedElements: zones,
      points: Math.max(0, score),
    });

    onVerify?.(score === dragElements.length);
    onSubmit();
    triggerHapticFeedback('heavy');
  };

  // Ergebnis-Styling basierend auf Theme
  const ratio = dragElements.length > 0 ? points / dragElements.length : 0;
  const resultStyles = 
    ratio === 1
      ? { 
          bg: theme.palette.success.main + '15', 
          border: theme.palette.success.main + '40', 
          text: theme.palette.success.main 
        }
      : ratio >= 0.5
      ? { 
          bg: theme.palette.grey[200] + '25', 
          border: theme.palette.grey[600] + '70', 
          text: theme.palette.grey[600] 
        }
      : { 
          bg: theme.palette.error.main + '15', 
          border: theme.palette.error.main + '40', 
          text: theme.palette.error.main 
        };

  const titleText = doc.body.innerHTML.split('<drag-drop>')[0]?.trim();

  return (
    <div
      ref={containerRef}
      style={{
        padding: '12px 14px',
        width: '100%',
        fontFamily: theme.typography.fontFamily,
        userSelect: 'none',
      }}
    >
      {/* Header */}
      {titleText && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: '12px' }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              color: theme.palette.text.primary,
              fontSize: '18px',
              lineHeight: 1.35,
              flex: 1,
              textAlign: 'center',
              m: 0,
              '@media (max-width: 480px)': { fontSize: '16px' },
            }}
            dangerouslySetInnerHTML={{ __html: titleText }}
          />

          <Tooltip title="Anleitung anzeigen/verstecken" placement="left" arrow>
            <IconButton
              onClick={() => setShowInstructions((s) => !s)}
              sx={{
                backgroundColor: showInstructions 
                  ? theme.palette.primary.main + '33' 
                  : theme.palette.primary.main + '1A',
                color: theme.palette.primary.main,
                width: 32,
                height: 32,
                ml: '8px',
                '&:hover': { 
                  backgroundColor: theme.palette.primary.main + '40', 
                  transform: 'scale(1.05)' 
                },
                transition: 'all 0.2s ease',
              }}
            >
              <InfoOutlined sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {/* Anleitung */}
      <Collapse in={showInstructions}>
        <Box
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main}1A, ${theme.palette.primary.main}0D)`,
            border: `1px solid ${theme.palette.primary.main}4D`,
            borderRadius: '6px',
            p: '10px',
            mb: '12px',
            fontSize: '13px',
            color: theme.palette.text.primary,
            textAlign: 'center',
          }}
        >
          <strong>{t("Instructions")}:</strong> {t("Select element → tap category. Tap assigned element = remove.")}
        </Box>
      </Collapse>

      {/* Statusbar */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
        {hasAnyAssignments && !hasVerified && (
          <button
            onClick={resetAll}
            style={{
              padding: '6px 12px',
              fontSize: 12,
              fontWeight: 500,
              backgroundColor: 'transparent',
              color: theme.palette.error.main,
              border: `1px solid ${theme.palette.error.main}33`,
              borderRadius: 8,
              cursor: 'pointer',
            }}
          >
            {t("Reset")}
          </button>
        )}
      </div>

      {/* Drop-Zonen */}
      <div style={{ marginBottom: 12 }}>
        {dropZones.map((zone) => {
          const zoneItems = zones[zone.id] || [];
          const interactive = !!selectedElement && !hasVerified;
          const colors = zoneColors(feedback[zone.id] ?? null, interactive);

          return (
            <div
              key={zone.id}
              onClick={() => interactive && placeInZone(zone.id)}
              style={{
                background: colors.bg,
                border: `2px solid ${colors.border}`,
                borderRadius: 10,
                padding: 12,
                marginBottom: 10,
                minHeight: 56,
                cursor: interactive ? 'pointer' : 'default',
                transition: 'all .2s ease',
              }}
            >
              <div style={{ 
                fontSize: 14, 
                fontWeight: 700, 
                marginBottom: 8, 
                color: theme.palette.text.primary 
              }}>
                {zone.label}
              </div>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, minHeight: 20, alignItems: 'center' }}>
                {zoneItems.map((itemId) => {
                  const element = dragElements.find((el) => el.id === itemId);
                  if (!element) return null;

                  const state = feedback[itemId];
                  const chipBg =
                    state === 'correct'
                      ? theme.palette.success.main + '20'
                      : state === 'incorrect'
                      ? theme.palette.error.main + '20'
                      : theme.palette.common.white;
                  const chipBorder =
                    state === 'correct'
                      ? theme.palette.success.main
                      : state === 'incorrect'
                      ? theme.palette.error.main
                      : theme.palette.secondary.dark;

                  return (
                    <button
                      key={itemId}
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFromZone(itemId, zone.id);
                      }}
                      disabled={hasVerified}
                      style={{
                        padding: '6px 10px',
                        fontSize: 12,
                        fontWeight: 600,
                        background: chipBg,
                        border: `1px solid ${chipBorder}`,
                        borderRadius: 8,
                        cursor: hasVerified ? 'default' : 'pointer',
                        position: 'relative',
                        color: theme.palette.text.primary,
                        transition: 'all .15s ease',
                      }}
                    >
                      {element.text}
                      {!hasVerified && (
                        <span
                          style={{
                            position: 'absolute',
                            top: -5,
                            right: -5,
                            fontSize: 10,
                            backgroundColor: theme.palette.error.main,
                            color: theme.palette.common.white,
                            borderRadius: '50%',
                            width: 16,
                            height: 16,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 700,
                          }}
                        >
                          ×
                        </span>
                      )}
                    </button>
                  );
                })}

                {zoneItems.length === 0 && (
                  <div
                    style={{
                      padding: 8,
                      color: theme.palette.text.secondary,
                      fontSize: 12,
                      fontStyle: 'italic',
                      border: `1px dashed ${theme.palette.secondary.dark}`,
                      borderRadius: 8,
                      width: '100%',
                      textAlign: 'center',
                      background: interactive ? theme.palette.info.main + '10' : 'transparent',
                    }}
                  >
                    {interactive ? t("Tap here") : ''}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Pool - nur anzeigen wenn Elemente vorhanden */}
      {availableElements.length > 0 && (
        <>
          <div style={{ 
            fontSize: 14, 
            fontWeight: 700, 
            color: theme.palette.text.secondary, 
            marginBottom: 8, 
            textAlign: 'center' 
          }}>
            {t("Available Elements")}
          </div>

          <div
            style={{
              backgroundColor: theme.palette.primary.main + '14',
              borderRadius: 10,
              padding: 12,
              marginBottom: 16,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 8,
              justifyContent: 'center',
            }}
          >
            {availableElements.map((elementId) => {
              const el = dragElements.find((e) => e.id === elementId);
              if (!el) return null;

              const isSelected = selectedElement === elementId;

              return (
                <button
                  key={elementId}
                  onClick={() => selectElement(elementId)}
                  disabled={hasVerified}
                  style={{
                    padding: '8px 14px',
                    fontSize: 13,
                    fontWeight: 600,
                    backgroundColor: isSelected 
                      ? theme.palette.info.main + '20' 
                      : theme.palette.common.white,
                    border: `2px solid ${isSelected 
                      ? theme.palette.info.main 
                      : theme.palette.secondary.dark}`,
                    borderRadius: 10,
                    cursor: hasVerified ? 'default' : 'pointer',
                    transition: 'all .15s ease',
                    transform: isSelected ? 'scale(1.03)' : 'scale(1)',
                    color: isSelected 
                      ? theme.palette.info.main 
                      : theme.palette.text.primary,
                    boxShadow: isSelected 
                      ? `0 1px 8px ${theme.palette.info.main}40` 
                      : '0 1px 3px rgba(0,0,0,.06)',
                  }}
                >
                  {el.text}
                </button>
              );
            })}
          </div>
        </>
      )}

      {/* Verify / Ergebnis */}
      <div style={{ textAlign: 'center' }}>
        {hasVerified ? (
          <div
            style={{
              backgroundColor: resultStyles.bg,
              border: `1px solid ${resultStyles.border}`,
              borderRadius: 10,
              padding: 16,
              color: resultStyles.text,
            }}
          >
            <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 6 }}>
              {t("Points earned")}: {points} {t("of")} {dragElements.length}
            </div>

            <div style={{ fontSize: 13, fontWeight: 600, opacity: 0.95 }}>
              {ratio === 1
                ? t("Perfect! All correctly assigned!")
                : ratio >= 0.5
                ? t("Good progress check the yellow/red fields.")
                : t("Not quite yet try again!")}
            </div>
          </div>
        ) : (
          <button
            onClick={handleVerify}
            disabled={!hasAnyAssignments}
            style={{
              width: '100%',
              padding: '12px 24px',
              fontSize: 16,
              fontWeight: 800,
              backgroundColor: hasAnyAssignments 
                ? theme.palette.primary.main 
                : theme.palette.secondary.light,
              color: theme.palette.common.white,
              border: 'none',
              borderRadius: 10,
              cursor: hasAnyAssignments ? 'pointer' : 'not-allowed',
              transition: 'all .15s ease',
              boxShadow: hasAnyAssignments 
                ? `0 4px 16px ${theme.palette.primary.main}40` 
                : 'none',
            }}
            onMouseOver={(e) => {
              if (hasAnyAssignments) {
                e.currentTarget.style.backgroundColor = theme.palette.primary.main + 'CC';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }
            }}
            onMouseOut={(e) => {
              if (hasAnyAssignments) {
                e.currentTarget.style.backgroundColor = theme.palette.primary.main;
                e.currentTarget.style.transform = 'translateY(0)';
              }
            }}
          >
            {t("Überprüfen")} ({totalAssigned} {t("of")} {dragElements.length} {t("assigned")})
          </button>
        )}
      </div>
    </div>
  );
};

export default DragAndDropMobile;